import { useEffect, useRef, useState } from 'react';
import { extractDominantColorFromUrl } from '../utils/dominantColor';

/**
 * Tracks the dominant color of whichever image URL is passed in (callers
 * should pass the current first/primary image of their image list). Re-runs
 * extraction whenever that URL changes — i.e. a new image is added and
 * becomes first, images are reordered, the first image is deleted so another
 * takes its place, or the primary image is swapped outright.
 *
 * Extraction failures are swallowed (logged in dominantColor.ts) and simply
 * leave the previously known color in place, since a save-time re-check
 * (see resolveDominantColorForSubmit) is the source of truth before submit.
 */
export function useDominantColor(
  primaryImageUrl: string | undefined,
  initialColor: string | null = null,
): [string | null, React.Dispatch<React.SetStateAction<string | null>>] {
  const [dominantColor, setDominantColor] = useState<string | null>(initialColor);
  const processedUrlRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (!primaryImageUrl) {
      processedUrlRef.current = undefined;
      setDominantColor(null);
      return;
    }
    if (processedUrlRef.current === primaryImageUrl) return;

    let cancelled = false;
    extractDominantColorFromUrl(primaryImageUrl).then((color) => {
      if (cancelled) return;
      processedUrlRef.current = primaryImageUrl;
      if (color) setDominantColor(color);
    });
    return () => {
      cancelled = true;
    };
  }, [primaryImageUrl]);

  return [dominantColor, setDominantColor];
}
